import React, { useState, useEffect } from "react";
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  Tooltip,
  useTheme,
  useMediaQuery,
  ListItemButton,
} from "@mui/material";
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  Inventory as InventoryIcon,
  People as PeopleIcon,
  CalendarMonth as CalendarIcon,
  Science as ScienceIcon,
  Settings as SettingsIcon,
  Logout as LogoutIcon,
  ChevronLeft as ChevronLeftIcon,
  Category as CategoryIcon,
  Build as BuildIcon,
} from "@mui/icons-material";
import { VpnKey as VpnKeyIcon } from "@mui/icons-material";
import { useNavigate, Link as RouterLink, useLocation } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import RealTimeNotificationStatus from "./RealTimeNotificationStatus";

// Responsive drawer width
const drawerWidth = {
  xs: "100%",
  sm: 240,
};

const Layout = ({
  children,
  title,
  onNotificationClick,
  unreadNotificationsCount,
}) => {
  const { userProfile, logout, authToken } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // Set initial drawer state based on screen size
  const [drawerOpen, setDrawerOpen] = useState(!isMobile);
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  // Close drawer automatically on mobile when route changes
  useEffect(() => {
    if (isMobile) {
      setDrawerOpen(false);
    }
  }, [location.pathname, isMobile]);

  // Handle profile menu
  const handleOpenUserMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseUserMenu = () => {
    setAnchorEl(null);
  };

  // Handle logout
  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  // Toggle drawer
  const toggleDrawer = () => {
    setDrawerOpen(!drawerOpen);
  };

  // Get navigation items based on user role
  const getNavItems = () => {
    // Debugging user role information
    console.log("Current user data:", {
      name: userProfile?.name,
      role: userProfile?.role,
      id: userProfile?.id,
      path: location.pathname,
    });

    // Common navigation items
    const items = [
      { text: "Dashboard", icon: <DashboardIcon />, path: "/dashboard" },
    ];

    // Role-specific items
    if (userProfile) {
      switch (userProfile.role) {
        case "developer-master":
          // Developer Master gets all admin features plus developer-specific features
          items.push(
            // System management features
            {
              text: "System Config",
              icon: <SettingsIcon sx={{ color: "#4caf50" }} />, // Green color
              path: "/developer-master",
              highlight: true, // For special styling
            },
            {
              text: "Departments",
              icon: <CategoryIcon sx={{ color: "#2196f3" }} />, // Blue color
              path: "/developer/departments",
            },
            {
              text: "Admin Assignment",
              icon: <PeopleIcon sx={{ color: "#ff9800" }} />, // Orange color
              path: "/developer/admins",
            },
            {
              text: "System Settings",
              icon: <SettingsIcon sx={{ color: "#9c27b0" }} />, // Purple color
              path: "/developer/settings",
            },
            {
              text: "Auto-Review Config",
              icon: <BuildIcon sx={{ color: "#795548" }} />, // Brown color
              path: "/developer/auto-review",
            },
            // Divider item
            { divider: true },
            // Admin features
            {
              text: "Inventory",
              icon: <InventoryIcon sx={{ color: "#f44336" }} />, // Red color
              path: "/inventory",
            },
            {
              text: "Categories",
              icon: <CategoryIcon sx={{ color: "#00bcd4" }} />, // Cyan color
              path: "/category-management",
            },
            {
              text: "Lab Management",
              icon: <ScienceIcon sx={{ color: "#e91e63" }} />, // Pink color
              path: "/lab-management",
            },
            {
              text: "Booking System",
              icon: <CalendarIcon sx={{ color: "#673ab7" }} />, // Deep purple
              path: "/booking-system",
            },
            {
              text: "Users",
              icon: <PeopleIcon sx={{ color: "#795548" }} />, // Brown color
              path: "/users",
            },
            {
              text: "Workers",
              icon: <BuildIcon sx={{ color: "#4caf50" }} />, // Green color
              path: "/admin/workers",
            },
            {
              text: "Maintenance",
              icon: <BuildIcon sx={{ color: "#607d8b" }} />, // Blue grey color
              path: "/admin/maintenance",
            }
          );
          break;
        case "developer":
          items.push(
            { text: "Inventory", icon: <InventoryIcon />, path: "/inventory" },
            {
              text: "Categories",
              icon: <CategoryIcon />,
              path: "/category-management",
            },
            {
              text: "Lab Management",
              icon: <ScienceIcon />,
              path: "/lab-management",
            },
            {
              text: "Booking System",
              icon: <CalendarIcon />,
              path: "/booking-system",
            },
            { text: "Users", icon: <PeopleIcon />, path: "/users" }
          );
          break;
        case "admin":
          items.push(
            { text: "Inventory", icon: <InventoryIcon />, path: "/inventory" },
            {
              text: "Categories",
              icon: <CategoryIcon />,
              path: "/category-management",
            },
            {
              text: "Lab Management",
              icon: <ScienceIcon />,
              path: "/lab-management",
            },
            {
              text: "Booking System",
              icon: <CalendarIcon />,
              path: "/booking-system",
            },
            { text: "Users", icon: <PeopleIcon />, path: "/users" },
            {
              text: "Workers",
              icon: <BuildIcon />,
              path: "/admin/workers",
            },
            {
              text: "Maintenance",
              icon: <BuildIcon />,
              path: "/admin/maintenance",
            }
          );
          break;
        case "hod":
          // Head of Department - Category-specific maintenance manager
          items.push(
            {
              text: "HOD Dashboard",
              icon: <DashboardIcon />,
              path: "/hod",
            },
            {
              text: "Team Management",
              icon: <PeopleIcon />,
              path: "/hod/team",
            },
            {
              text: "Category Requests",
              icon: <BuildIcon />,
              path: "/hod/requests",
            }
          );
          break;
        case "manager":
          // Global maintenance manager (maintenance superuser)
          items.push(
            {
              text: "Manager Dashboard",
              icon: <DashboardIcon />,
              path: "/manager",
            },
            {
              text: "Maintenance",
              icon: <BuildIcon />,
              path: "/admin/maintenance",
            },
            {
              text: "Workers",
              icon: <BuildIcon />,
              path: "/admin/workers",
            }
          );
          break;
        case "faculty":
          items.push(
            {
              text: "Booking System",
              icon: <CalendarIcon />,
              path: "/booking-system",
            },
            {
              text: "Inventory",
              icon: <InventoryIcon />,
              path: "/inventory",
            },
            {
              text: "User Management",
              icon: <PeopleIcon />,
              path: "/faculty/users",
            },
            {
              text: "Registration Tokens",
              icon: <VpnKeyIcon />,
              path: "/faculty/tokens",
            }
          );
          break;
        case "scholar":
          items.push(
            {
              text: "Booking System",
              icon: <CalendarIcon />,
              path: "/booking-system",
            },
            { text: "Inventory", icon: <InventoryIcon />, path: "/inventory" }
          );
          break;
        case "student":
          items.push(
            {
              text: "Lab Sessions",
              icon: <CalendarIcon />,
              path: "/lab-sessions",
            },
            { text: "Resources", icon: <InventoryIcon />, path: "/resources" }
          );
          break;
        default:
          break;
      }
    }

    return items;
  };

  const navItems = getNavItems();

  return (
    <Box sx={{ display: "flex", height: "100vh", overflow: "hidden" }}>
      {/* App Bar */}
      <AppBar
        position="fixed"
        elevation={1}
        sx={{
          zIndex: theme.zIndex.drawer + 1,
          transition: theme.transitions.create(["width", "margin"], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
          ...(drawerOpen &&
            !isMobile && {
              marginLeft: drawerWidth.sm,
              width: `calc(100% - ${drawerWidth.sm}px)`,
              transition: theme.transitions.create(["width", "margin"], {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.enteringScreen,
              }),
            }),
        }}
      >
        <Toolbar sx={{ px: { xs: 1, sm: 2 } }}>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            onClick={toggleDrawer}
            edge="start"
            sx={{ mr: 2 }}
          >
            {drawerOpen && !isMobile ? <ChevronLeftIcon /> : <MenuIcon />}
          </IconButton>
          <Typography
            variant="h6"
            component="div"
            sx={{
              flexGrow: 1,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {title || "SEED Organizr"}
          </Typography>

          {/* Real-time Notification System for Admin, Manager, HOD, and Developer Master users */}
          {(userProfile?.role === "admin" ||
            userProfile?.role === "manager" ||
            userProfile?.role === "hod" ||
            userProfile?.role === "developer-master") && (
            <RealTimeNotificationStatus
              token={authToken}
              onNotificationClick={(notification) => {
                console.log("Notification clicked:", notification);
                // Handle notification click - could navigate to relevant page
                if (notification.data?.requestId) {
                  navigate(
                    `/maintenance/requests/${notification.data.requestId}`
                  );
                } else if (notification.data?.itemId) {
                  navigate(`/inventory/${notification.data.itemId}`);
                }
              }}
            />
          )}

          {/* User menu */}
          <Box>
            <Tooltip title="Account settings">
              <IconButton onClick={handleOpenUserMenu} sx={{ p: 0 }}>
                <Avatar
                  alt={userProfile?.name || "User"}
                  src={userProfile?.avatarUrl || "/logo1.svg"}
                  sx={{ bgcolor: theme.palette.primary.main }}
                >
                  {userProfile?.name?.[0] || "?"}
                </Avatar>
              </IconButton>
            </Tooltip>
            <Menu
              anchorEl={anchorEl}
              open={open}
              onClose={handleCloseUserMenu}
              sx={{ mt: "45px" }}
              transformOrigin={{ horizontal: "right", vertical: "top" }}
              anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
            >
              <MenuItem>
                <Typography textAlign="center">
                  {userProfile?.name || "User"} ({userProfile?.role || "guest"})
                </Typography>
              </MenuItem>
              <MenuItem component={RouterLink} to="/profile">
                <ListItemIcon>
                  <SettingsIcon fontSize="small" />
                </ListItemIcon>
                <Typography textAlign="center">Profile</Typography>
              </MenuItem>
              <Divider />
              <MenuItem onClick={handleLogout}>
                <ListItemIcon>
                  <LogoutIcon fontSize="small" />
                </ListItemIcon>
                <Typography textAlign="center">Logout</Typography>
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Navigation Drawer */}
      <Drawer
        variant={isMobile ? "temporary" : "persistent"}
        open={drawerOpen}
        onClose={isMobile ? toggleDrawer : undefined}
        PaperProps={{
          sx: {
            backgroundColor:
              theme.palette.mode === "dark" ? "#1a2027" : "#f8f9fa",
            borderRight: `1px solid ${theme.palette.divider}`,
            boxShadow: isMobile
              ? theme.shadows[8]
              : "0px 3px 15px rgba(0,0,0,0.1)",
            borderRadius: isMobile ? 0 : "0 16px 16px 0",
          },
        }}
        sx={{
          width: isMobile ? "100%" : drawerWidth.sm,
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width: isMobile ? drawerWidth.xs : drawerWidth.sm,
            boxSizing: "border-box",
          },
        }}
      >
        <Toolbar />
        <Box
          sx={{
            overflow: "auto",
            mt: 2,
            height: "calc(100% - 64px)",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <List component="nav" sx={{ px: 1 }}>
            {navItems.map((item, idx) =>
              item.divider ? (
                <Divider key={`divider-${idx}`} sx={{ my: 1 }} />
              ) : (
                <ListItem disablePadding key={item.text}>
                  <ListItemButton
                    component={RouterLink}
                    to={item.path}
                    selected={location.pathname === item.path}
                    sx={{
                      borderRadius: 2,
                      mb: 0.8,
                      transition: "all 0.2s ease-in-out",
                      padding: "10px 16px",
                      "&.Mui-selected": {
                        backgroundColor: theme.palette.primary.main + "20",
                        boxShadow: "0 2px 10px rgba(0,0,0,0.05)",
                        "&:hover": {
                          backgroundColor: theme.palette.primary.main + "30",
                          transform: "translateX(5px)",
                        },
                      },
                      "&:hover": {
                        transform: "translateX(5px)",
                        backgroundColor: theme.palette.action.hover,
                      },
                      ...(item.highlight && {
                        background:
                          "linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)",
                        boxShadow: "0 3px 5px 2px rgba(33, 203, 243, .3)",
                        color: "white",
                        "& .MuiListItemIcon-root": {
                          color: "white",
                        },
                        "& .MuiListItemText-primary": {
                          color: "white",
                          fontWeight: "bold",
                        },
                        "&:hover": {
                          background:
                            "linear-gradient(45deg, #1976D2 30%, #21A6F3 90%)",
                          transform: "translateX(5px) scale(1.02)",
                        },
                      }),
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: 40,
                        color:
                          location.pathname === item.path
                            ? theme.palette.primary.main
                            : "inherit",
                      }}
                    >
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={item.text}
                      primaryTypographyProps={{
                        fontWeight:
                          location.pathname === item.path ? "medium" : "normal",
                        color:
                          location.pathname === item.path
                            ? theme.palette.primary.main
                            : "inherit",
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              )
            )}
          </List>

          <Box sx={{ flexGrow: 1 }} />

          <Box
            sx={{
              p: 2,
              borderTop: `1px solid ${theme.palette.divider}`,
              display: { xs: "none", sm: "block" },
            }}
          >
            <Typography variant="caption" color="text.secondary">
              SEED Organizr v1.0
            </Typography>
          </Box>
        </Box>
      </Drawer>

      {/* Main content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          height: "100vh",
          overflow: "auto",
          width: { xs: "100%", sm: "auto" },
          bgcolor: "background.paper",
          transition: theme.transitions.create("margin", {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
          ...(drawerOpen &&
            !isMobile && {
              transition: theme.transitions.create("margin", {
                easing: theme.transitions.easing.easeOut,
                duration: theme.transitions.duration.enteringScreen,
              }),
              marginLeft: 0,
            }),
          px: { xs: 1, sm: 2, md: 3, lg: 4 }, // Adaptive padding
        }}
      >
        <Toolbar />
        {children}
      </Box>
    </Box>
  );
};

export default Layout;
